// AccountSettings.jsx
import React, { useState, useEffect, useRef } from "react";
import { CustomerAuthService } from "../../services/CustomerAuthServices";

/**
 * Props:
 *  - userData       : object  — { name, email }
 *  - setUserData    : (updater) => void  — to sync name back to parent
 *  - onBack         : () => void  — go back to overview
 */
const AccountSettings = ({ userData, setUserData, onBack }) => {
  const settingsRootRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      settingsRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  // ── Local form state ──
  const [settingsName, setSettingsName] = useState(userData?.name || "");
  const [settingsCurrentPw, setSettingsCurrentPw] = useState("");
  const [settingsNewPw, setSettingsNewPw] = useState("");
  const [settingsConfirmPw, setSettingsConfirmPw] = useState("");

  const [showCurPw, setShowCurPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConPw, setShowConPw] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState({ type: "", text: "" });

  // Keep name in sync if userData updates externally
  useEffect(() => {
    if (userData?.name) setSettingsName(userData.name);
  }, [userData?.name]);

  const pickErr = (err) => {
    const d = err?.response?.data;
    if (d?.errors) {
      const k = Object.keys(d.errors)[0];
      if (d.errors[k]?.[0]) return d.errors[k][0];
    }
    return d?.message || null;
  };

  const hasChanges =
    settingsName.trim() !== (userData?.name || "") ||
    settingsCurrentPw ||
    settingsNewPw ||
    settingsConfirmPw;

  const handleSave = async (e) => {
    e.preventDefault();
    setSettingsMsg({ type: "", text: "" });

    const payload = {};

    if (settingsName.trim() && settingsName.trim() !== userData?.name) {
      payload.name = settingsName.trim();
    }

    const wantsPasswordChange = settingsCurrentPw || settingsNewPw || settingsConfirmPw;
    if (wantsPasswordChange) {
      if (!settingsCurrentPw)
        return setSettingsMsg({ type: "err", text: "Please enter your current password." });
      if (!settingsNewPw)
        return setSettingsMsg({ type: "err", text: "Please enter a new password." });
      if (settingsNewPw.length < 6)
        return setSettingsMsg({ type: "err", text: "New password must be at least 6 characters." });
      if (settingsNewPw !== settingsConfirmPw)
        return setSettingsMsg({ type: "err", text: "New passwords do not match." });
      payload.current_password = settingsCurrentPw;
      payload.password = settingsNewPw;
      payload.password_confirmation = settingsConfirmPw;
    }

    if (Object.keys(payload).length === 0)
      return setSettingsMsg({ type: "err", text: "No changes detected." });

    try {
      setSettingsLoading(true);
      const res = await CustomerAuthService.updateProfile(payload);
      setUserData((prev) => ({ ...prev, ...res.user }));
      setSettingsMsg({ type: "ok", text: res.message || "Profile updated successfully!" });
      setSettingsCurrentPw("");
      setSettingsNewPw("");
      setSettingsConfirmPw("");
    } catch (err) {
      setSettingsMsg({ type: "err", text: pickErr(err) || "Failed to update profile." });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCancel = () => {
    setSettingsName(userData?.name || "");
    setSettingsCurrentPw("");
    setSettingsNewPw("");
    setSettingsConfirmPw("");
    setSettingsMsg({ type: "", text: "" });
  };

  return (
    <>
      {/* ── Topbar ── */}
      <div className="ap-topbar" ref={settingsRootRef}>
        <span className="ap-topbar-title">Account Settings</span>
        <div className="ap-topbar-right" />
      </div>

      {/* ── Body ── */}
      <div className="ap-body">
        {/* Back breadcrumb */}
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#6b7280",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            <i className="ph ph-arrow-left" style={{ fontSize: 15 }} />
            Back to Overview
          </button>
        </div>

        {settingsMsg?.text && (
          <div className={`ap-alert ${settingsMsg.type === "err" ? "err" : "ok"}`}>
            <i
              className={`ph ${settingsMsg.type === "err" ? "ph-warning-circle" : "ph-check-circle"}`}
              style={{ fontSize: 16, flexShrink: 0 }}
            />
            {settingsMsg.text}
          </div>
        )}

        <form onSubmit={handleSave}>
          {/* ── Profile card ── */}
          <div className="ap-section" style={{ marginBottom: 20 }}>
            <div className="ap-section-hd">
              <div>
                <div className="ap-section-title">Profile Details</div>
                <div className="ap-section-sub">Update your display name</div>
              </div>
            </div>
            <div className="ap-section-bd">
              <div className="ap-settings-grid">
                {/* Name */}
                <div className="ap-settings-form-field">
                  <label className="ap-settings-label">Full Name</label>
                  <input
                    className="ap-settings-input"
                    type="text"
                    placeholder="Your full name"
                    value={settingsName}
                    onChange={(e) => setSettingsName(e.target.value)}
                    disabled={settingsLoading}
                  />
                </div>
                {/* Email (locked) */}
                <div className="ap-settings-form-field">
                  <label className="ap-settings-label">Email Address</label>
                  <input
                    className="ap-settings-input"
                    type="email"
                    value={userData?.email || ""}
                    disabled
                  />
                  <div className="ap-settings-hint locked">
                    <i className="ph ph-lock-simple" style={{ fontSize: 12 }} />
                    Email address cannot be changed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Password card ── */}
          <div className="ap-section" style={{ marginBottom: 20 }}>
            <div className="ap-section-hd">
              <div>
                <div className="ap-section-title">Change Password</div>
                <div className="ap-section-sub">Leave blank to keep your current password</div>
              </div>
            </div>
            <div className="ap-section-bd">
              <div className="ap-password-section">
                <div className="ap-password-section-title">
                  <i className="ph ph-lock-key" style={{ fontSize: 13 }} />
                  Password Update
                </div>

                {/* Current password */}
                <div className="ap-settings-form-field">
                  <label className="ap-settings-label">Current Password</label>
                  <div className="ap-settings-input-wrap">
                    <input
                      className="ap-settings-input"
                      type={showCurPw ? "text" : "password"}
                      placeholder="Enter current password"
                      value={settingsCurrentPw}
                      onChange={(e) => setSettingsCurrentPw(e.target.value)}
                      disabled={settingsLoading}
                    />
                    <button
                      type="button"
                      className="ap-settings-eye"
                      onClick={() => setShowCurPw(!showCurPw)}
                    >
                      <i className={`ph ${showCurPw ? "ph-eye" : "ph-eye-slash"}`} />
                    </button>
                  </div>
                </div>

                <div className="ap-settings-grid">
                  {/* New password */}
                  <div className="ap-settings-form-field">
                    <label className="ap-settings-label">New Password</label>
                    <div className="ap-settings-input-wrap">
                      <input
                        className="ap-settings-input"
                        type={showNewPw ? "text" : "password"}
                        placeholder="Min. 6 characters"
                        value={settingsNewPw}
                        onChange={(e) => setSettingsNewPw(e.target.value)}
                        disabled={settingsLoading}
                      />
                      <button
                        type="button"
                        className="ap-settings-eye"
                        onClick={() => setShowNewPw(!showNewPw)}
                      >
                        <i className={`ph ${showNewPw ? "ph-eye" : "ph-eye-slash"}`} />
                      </button>
                    </div>
                  </div>

                  {/* Confirm new password */}
                  <div className="ap-settings-form-field">
                    <label className="ap-settings-label">Confirm New Password</label>
                    <div className="ap-settings-input-wrap">
                      <input
                        className="ap-settings-input"
                        type={showConPw ? "text" : "password"}
                        placeholder="Repeat new password"
                        value={settingsConfirmPw}
                        onChange={(e) => setSettingsConfirmPw(e.target.value)}
                        disabled={settingsLoading}
                      />
                      <button
                        type="button"
                        className="ap-settings-eye"
                        onClick={() => setShowConPw(!showConPw)}
                      >
                        <i className={`ph ${showConPw ? "ph-eye" : "ph-eye-slash"}`} />
                      </button>
                    </div>
                    {settingsNewPw && settingsConfirmPw && settingsNewPw !== settingsConfirmPw && (
                      <div className="ap-settings-hint" style={{ color: "#dc2626" }}>
                        <i className="ph ph-warning-circle" style={{ fontSize: 12 }} />
                        Passwords don't match
                      </div>
                    )}
                    {settingsNewPw && settingsConfirmPw && settingsNewPw === settingsConfirmPw && (
                      <div className="ap-settings-hint" style={{ color: "#16a34a" }}>
                        <i className="ph ph-check-circle" style={{ fontSize: 12 }} />
                        Passwords match
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Save row ── */}
          <div className="ap-settings-save-row">
            <button
              type="button"
              className="ap-settings-cancel-btn"
              onClick={handleCancel}
              disabled={settingsLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ap-settings-save-btn"
              disabled={settingsLoading || !hasChanges}
            >
              {settingsLoading ? (
                <>
                  <i
                    className="ph ph-circle-notch"
                    style={{
                      animation: "ap-spin .7s linear infinite",
                      display: "inline-block",
                    }}
                  />{" "}
                  Saving…
                </>
              ) : (
                <>
                  <i className="ph ph-floppy-disk" /> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AccountSettings;